import {
  Button,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { services } from "../data"; // Import services data
import { addDoc, collection, db } from "../firebase";

const PortfolioUpload = ({ isOpen, onClose, fetchPortfolio }) => {
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [tags, setTags] = useState([]); // Store selected service tags
  const [photoFiles, setPhotoFiles] = useState([]);
  const [beforePhotoFiles, setBeforePhotoFiles] = useState([]);
  const [afterPhotoFiles, setAfterPhotoFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  /** Handle selecting a service as a tag */
  const handleSelectTag = (serviceName) => {
    if (!tags.includes(serviceName)) {
      setTags([...tags, serviceName]); // Add only if it's not already selected
    }
  };

  /** Handle removing a tag */
  const handleRemoveTag = (index) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  /** Store selected files in state (instead of uploading immediately) */
  const handleFileSelection = (event, setFiles) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles(selectedFiles); // Store files for later upload
  };

  /** Upload images to Firebase Storage & return URLs */
  const uploadFiles = async (files) => {
    if (files.length === 0) return [];

    const urls = [];
    const storage = getStorage();

    for (const file of files) {
      try {
        const fileRef = ref(storage, `portfolio/${file.name}`);
        await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(fileRef);
        urls.push(downloadURL);
      } catch (error) {
        console.error("Error uploading file:", error);
        toast({
          title: "Upload failed",
          description: "There was an issue uploading the images.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }

    return urls;
  };

  /** Upload the portfolio item to Firestore */
  const handleSubmit = async () => {
    if (!name || !date || tags.length === 0) {
      toast({
        title: "Missing required fields",
        description: "Please fill in all required details before uploading.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      // Upload images only when "Upload" button is clicked
      // const beforePhotos = await uploadFiles(beforePhotoFiles);
      // const afterPhotos = await uploadFiles(afterPhotoFiles);
      const photos = await uploadFiles(photoFiles);

      await addDoc(collection(db, "portfolio"), {
        name,
        date,
        tags,
        photos,
        // beforePhotos,
        // afterPhotos,
      });

      toast({
        title: "Portfolio item added!",
        description: "Your portfolio item was successfully uploaded.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      fetchPortfolio();
      // Reset form fields
      setName("");
      setDate("");
      setTags([]);
      setAfterPhotoFiles([]);
      // setBeforePhotoFiles([]);
      // setAfterPhotoFiles([]);
      onClose();
    } catch (error) {
      console.error("Error adding portfolio item:", error);
      toast({
        title: "Error",
        description: "There was an issue adding the portfolio item.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Portfolio Item</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} width="100%" align={"start"}>
            {/* Project Name */}
            <Input
              placeholder="Project Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              isDisabled={loading}
            />
            {/* Completion Date */}
            <Input
              placeholder="Completion Date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              type="date"
              isDisabled={loading}
            />
            {/* Service Selection Dropdown */}
            <Menu isDisabled={loading}>
              <MenuButton
                as={Button}
                rightIcon={<FaChevronDown />}
                colorScheme="blue"
                fontWeight={"normal"}
                width={"100%"}
                size={"sm"}
                isDisabled={loading}
              >
                Select Tag(s)
              </MenuButton>
              <MenuList>
                {services.map((service, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => handleSelectTag(service.name)}
                  >
                    {service.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            {/* Show selected tags */}
            <HStack spacing={2} wrap="wrap">
              {tags.map((tag, index) => (
                <Tag key={index} size="sm" colorScheme="blue" borderRadius="sm">
                  <TagLabel>{tag}</TagLabel>
                  <TagCloseButton
                    isDisabled={loading}
                    onClick={() => handleRemoveTag(index)}
                  />
                </Tag>
              ))}
            </HStack>
            <Text fontSize={"xs"} fontWeight={"bold"} textAlign={"start"}>
              Add Photos (Max 6)
            </Text>
            <Input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileSelection(e, setPhotoFiles)}
              isDisabled={loading}
            />

            {/* <Text fontSize={"xs"} fontWeight={"bold"} textAlign={"start"}>
              Before Photos
            </Text>
            <Input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileSelection(e, setBeforePhotoFiles)}
              isDisabled={loading}
            />

            <Text fontSize={"xs"} fontWeight={"bold"} textAlign={"start"}>
              After Photos
            </Text>
            <Input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileSelection(e, setAfterPhotoFiles)}
              isDisabled={loading}
            /> */}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="green"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Uploading..."
            fontSize={"xs"}
          >
            Upload
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PortfolioUpload;
